/**
 * The courseware player's data: the outline, where the learner left off, and the block
 * the platform renders for them.
 *
 * Every endpoint here already ships with Open edX. Nothing is custom, and — this is the
 * part worth stating plainly — nothing here reimplements an XBlock. Open edX renders
 * videos, problems, drag-and-drops, LTI tools and third-party blocks as server-side HTML
 * with each block's own CSS and JavaScript; the set of block types is open-ended, so any
 * player that drew the content itself would be a drawing of a course and would diverge the
 * first time someone installed a new block. We own the chrome, the platform owns the pane.
 *
 * Shapes below were read off the live server, not from documentation.
 *
 * ---------------------------------------------------------------------------------
 * WHICH ENDPOINT ANSWERS WHICH QUESTION. Four endpoints, four different questions, and
 * picking the wrong one is the most expensive mistake available here:
 *
 *   /api/course_home/course_metadata/{id}   MAY I be here?   (in catalogue.ts — it is
 *                                           the same call the course page already makes)
 *   /api/course_home/v1/navigation/{id}     WHAT is in here?  sections → subsections →
 *                                           units, WITH completion. The only built-in
 *                                           endpoint that reaches unit depth in one call.
 *   /api/courseware/resume/{id}             WHERE was I?
 *   /api/courseware/course/{id}             WHEN does it run? (dates, enrolment track)
 *
 * Two near-misses, recorded so nobody re-tries them:
 *
 *   /api/course_home/outline/{id} stops at SUBSECTION depth — every `sequential` comes
 *   back with `children: []`. Verified live. It is the course-home tab's payload, not a
 *   player's; a sidebar built on it can never show units.
 *
 *   /api/learning_sequences/v1/course_outline/{id} is ordered and release-aware but also
 *   stops at sequences, and carries no completion at all. The official micro-frontend
 *   uses it for ordering and then fetches every sequence separately. `navigation` gives
 *   ordering, unit depth and completion in ONE request, so that is what this uses.
 * ---------------------------------------------------------------------------------
 */

import { openedx } from './openedx';
import { isResizeMessage } from './unit';

/** Course and usage keys both contain ':' and '+', so every one is encoded. */
const key = (value: string) => encodeURIComponent(value);

/* ------------------------------------------------------------------ outline ----- */

/**
 * One node of `GET /api/course_home/v1/navigation/{course_id}`.
 *
 * The payload is `{ blocks: { <usage key>: block } }` — a FLAT MAP, not a tree. Order
 * lives only in each block's `children` array, so the tree has to be walked from the
 * course root; iterating the map's own keys gives you the course in whatever order the
 * serializer happened to emit, which is not the author's order.
 */
interface NavigationBlock {
  id: string;
  /** 'course' | 'chapter' (section) | 'sequential' (subsection) | 'vertical' (unit) */
  type: string;
  display_name: string;
  /** Null on leaves rather than an empty array, so it is never assumed to be iterable. */
  children: string[] | null;
  complete: boolean;
  /**
   * The platform's own summary of a unit's content: 'video', 'problem', 'other'…
   *
   * Set on verticals only, and derived from the unit's children — so it is the platform's
   * answer to "what kind of thing is this", which is exactly what the outline row wants to
   * show. Null everywhere else.
   */
  icon: string | null;
  /** The author asked for this block to be left out of the outline. Honoured below. */
  hide_from_toc: boolean;
  /**
   * On the course block: there IS content, and it is scheduled to open later.
   *
   * The distinction this draws is the difference between two identical-looking empty
   * outlines. A learner enrolled in a course whose sections have future release dates gets
   * a payload with ONE block — the course, `children: []` — exactly like a course nobody
   * has written yet. Verified live: the same course returns three subsections to staff and
   * an empty course block plus `has_scheduled_content: true` to an enrolled learner.
   * Without this flag the player would tell them the course is empty, which is false and
   * makes the academy look broken.
   */
  has_scheduled_content: boolean | null;
  /**
   * `completion` counts the COMPLETED children, `completable_children` the ones that can
   * be completed. Present on containers; on a unit both are 0, which is why unit progress
   * is counted from `complete` instead.
   */
  completion_stat?: { completion: number; completable_children: number };
}

interface NavigationResponse {
  blocks: Record<string, NavigationBlock>;
}

export interface OutlineUnit {
  id: string;
  title: string;
  complete: boolean;
  /** Raw platform value; `unitIcon()` turns it into something to draw. */
  icon: string | null;
  sectionId: string;
  sectionTitle: string;
  subsectionId: string;
  subsectionTitle: string;
  /** 1-based, within the subsection — the "Unit 2 of 4" a learner reads. */
  position: number;
  positionOf: number;
}

export interface OutlineSubsection {
  id: string;
  title: string;
  complete: boolean;
  units: OutlineUnit[];
  done: number;
}

export interface OutlineSection {
  id: string;
  title: string;
  complete: boolean;
  subsections: OutlineSubsection[];
}

export interface CourseOutline {
  title: string;
  sections: OutlineSection[];
  /**
   * Every unit in course order, flattened.
   *
   * This is what makes Previous/Next cross subsection and section boundaries without any
   * special case: the neighbours of a unit are its neighbours in this array, whether or
   * not they live in the same subsection. The official micro-frontend reaches the same
   * result the long way round, by walking to the adjacent SEQUENCE and then resolving a
   * `first`/`last` marker into a real unit id once that sequence has loaded.
   */
  units: OutlineUnit[];
  /** Completed units and total units — the sidebar's progress figure. */
  done: number;
  total: number;
  /** Content exists but has not been released yet. See `has_scheduled_content`. */
  scheduled: boolean;
}

/**
 * The whole outline, as a tree, in the author's order.
 *
 * Requires enrolment: the endpoint answers 403 to a learner who is not in the course.
 * Callers gate on `course_access.has_access` first so that a 403 here is a genuine fault
 * rather than the ordinary "you have not joined yet" case, which deserves its own screen.
 */
export async function getCourseOutline(courseId: string): Promise<CourseOutline> {
  const { blocks } = await openedx.get<NavigationResponse>(
    `/api/course_home/v1/navigation/${key(courseId)}`,
  );

  const root = Object.values(blocks).find((block) => block.type === 'course');
  if (!root) {
    // A course with no root block is not an empty course, it is a broken response. Saying
    // so beats rendering an empty sidebar that looks like an empty course.
    throw new Error('The course outline came back without a course block.');
  }

  const childrenOf = (block: NavigationBlock | undefined): NavigationBlock[] =>
    (block?.children ?? []).flatMap((id) => {
      const child = blocks[id];
      // `hide_from_toc` is the author saying "do not list this". A block missing from the
      // map at all is a dangling reference, which is skipped for the same reason.
      return child && !child.hide_from_toc ? [child] : [];
    });

  const units: OutlineUnit[] = [];

  const sections: OutlineSection[] = childrenOf(root).map((section) => ({
    id: section.id,
    title: section.display_name,
    complete: section.complete,
    subsections: childrenOf(section).map((subsection) => {
      const rows = childrenOf(subsection);
      const unitsHere = rows.map((unit, position) => {
        const record: OutlineUnit = {
          id: unit.id,
          title: unit.display_name,
          complete: unit.complete,
          icon: unit.icon,
          sectionId: section.id,
          sectionTitle: section.display_name,
          subsectionId: subsection.id,
          subsectionTitle: subsection.display_name,
          position: position + 1,
          positionOf: rows.length,
        };
        units.push(record);
        return record;
      });

      return {
        id: subsection.id,
        title: subsection.display_name,
        complete: subsection.complete,
        units: unitsHere,
        done: unitsHere.filter((unit) => unit.complete).length,
      };
    }),
  }));

  return {
    title: root.display_name,
    sections,
    units,
    done: units.filter((unit) => unit.complete).length,
    total: units.length,
    scheduled: root.has_scheduled_content === true,
  };
}

/** The unit before and after this one, anywhere in the course. Null at either end. */
export function neighbours(
  outline: CourseOutline,
  unitId: string,
): { previous: OutlineUnit | null; next: OutlineUnit | null } {
  const at = outline.units.findIndex((unit) => unit.id === unitId);
  if (at === -1) return { previous: null, next: null };
  return {
    previous: outline.units[at - 1] ?? null,
    next: outline.units[at + 1] ?? null,
  };
}

/* ------------------------------------------------------------------- resume ----- */

/**
 * `GET /api/courseware/resume/{course_id}`.
 *
 * All three fields are null for someone who has never opened the course — that is a 200,
 * not a 404, so "no resume point" is a value to handle rather than an error to catch.
 * `section_id` is the SUBSECTION (sequential) despite its name; `unit_id` is the vertical,
 * which is the only one this player needs.
 */
interface ResumePoint {
  block_id: string | null;
  section_id: string | null;
  unit_id: string | null;
}

/**
 * Where to send a learner who asked for the course rather than a unit.
 *
 * Three answers in descending order of how much they know about this person:
 *
 *   1. the platform's own resume point — genuinely where they left off;
 *   2. the first unit they have not finished — right after a fresh enrolment, and right
 *      after the platform forgets (resume is derived from completion records, so it is
 *      empty until the first block is completed);
 *   3. the first unit in the course.
 *
 * The resume point is checked against the outline before it is used. It can name a unit
 * the learner can no longer reach — content gets deleted, hidden, or moved behind a
 * prerequisite between visits — and sending someone to a unit that is not in their own
 * outline is how a player ends up rendering an empty pane with no way back.
 */
export async function resumeUnitId(
  courseId: string,
  outline: CourseOutline,
): Promise<string | null> {
  if (outline.units.length === 0) return null;

  try {
    const resume = await openedx.get<ResumePoint>(`/api/courseware/resume/${key(courseId)}`);
    if (resume.unit_id && outline.units.some((unit) => unit.id === resume.unit_id)) {
      return resume.unit_id;
    }
  } catch {
    // Resume is a convenience, not a requirement. A learner who cannot be told where they
    // left off should still be let into the course, at the top.
  }

  const unfinished = outline.units.find((unit) => !unit.complete);
  return (unfinished ?? outline.units[0])?.id ?? null;
}

/* ----------------------------------------------------------------- schedule ----- */

/**
 * `GET /api/courseware/course/{course_id}` — the course's own facts.
 *
 * Worth knowing what is NOT here: this endpoint carries no `course_access` and no
 * `is_staff`, unlike the same-named payload in the official micro-frontend's fixtures.
 * Verified live on release/verawood — the response has 51 keys and neither of those is
 * among them. Whether the learner may be here is answered by
 * `/api/course_home/course_metadata/{id}` (see `getCourseMetadata` in catalogue.ts), and
 * asking this endpoint instead yields `undefined`, which reads as "no access problem" and
 * silently opens the courseware to everyone.
 */
export interface CoursewareCourse {
  id: string;
  name: string;
  start: string | null;
  end: string | null;
  pacing: string;
  /** `is_active` is the enrolment; `mode` is the track ('audit', 'verified', …). */
  enrollment: { mode: string | null; is_active: boolean };
  display_org_with_default: string;
}

export const getCoursewareCourse = (courseId: string) =>
  openedx.get<CoursewareCourse>(`/api/courseware/course/${key(courseId)}`);

/** A course whose end date has passed. Still readable — but it should say so. */
export function hasEnded(course: CoursewareCourse | undefined): boolean {
  if (!course?.end) return false;
  const end = Date.parse(course.end);
  return !Number.isNaN(end) && end < Date.now();
}

/* ----------------------------------------------------------------- sequence ----- */

/**
 * `GET /api/courseware/sequence/{sequence_id}` — one subsection's own rules.
 *
 * The outline already provides the unit list, so this is fetched for the ONE subsection
 * being viewed, and only for the things the outline cannot know:
 *
 *   `gated_content.gated`  a prerequisite subsection has not been passed. The platform
 *                          still lists the units; it will not render them.
 *   `is_time_limited`      a timed or proctored exam. Those carry rules this player does
 *                          not implement, so it declines to start one rather than showing
 *                          a learner an exam whose timer nothing is running.
 *   `banner_text`          the platform's own notice for this subsection, already written
 *                          for a learner.
 *   `format`               'Homework', 'Lab', … Passed to the render so graded blocks
 *                          label themselves the way the rest of the platform does.
 */
export interface SequenceMetadata {
  item_id: string;
  display_name: string;
  format: string | null;
  banner_text: string | null;
  is_time_limited: boolean;
  is_proctored: boolean;
  is_hidden_after_due: boolean;
  show_completion: boolean;
  gated_content: {
    gated: boolean;
    prereq_id: string | null;
    prereq_url: string | null;
    prereq_section_name: string | null;
    gated_section_name: string | null;
  };
  items: {
    id: string;
    page_title: string;
    type: string;
    complete: boolean | null;
    bookmarked: boolean;
    graded: boolean;
    /** The platform's own breadcrumb: "Section > Subsection > Unit". */
    path: string;
  }[];
}

export const getSequenceMetadata = (sequenceId: string) =>
  openedx.get<SequenceMetadata>(`/api/courseware/sequence/${key(sequenceId)}?preview=0`);

/* ------------------------------------------------------------- the rendering ---- */

/**
 * Where the platform renders one unit for this learner.
 *
 * The parameters are the official player's, verbatim from
 * `frontend-app-learning/src/courseware/course/sequence/Unit/urls.ts`, and each one earns
 * its place:
 *
 *   view=student_view    the learner's view of the block, not the author's
 *   show_title=0         we draw the title in our own chrome; the platform's would be a
 *                        second copy of it inside the pane
 *   show_bookmark=0      the bookmark control belongs to a feature this app does not have
 *   recheck_access=1     re-run access checks per block, so a gated component inside an
 *                        otherwise open unit is withheld rather than shown
 *   preview=0            published content, which is what a learner is entitled to see
 *   format=…             the subsection's grading format, so graded blocks label
 *                        themselves consistently with the rest of the platform
 *
 * ---------------------------------------------------------------------------------
 * ORIGIN. This is built by `xblockEmbedUrl`, which yields `${VITE_LMS_ORIGIN}/xblock/…`
 * and therefore a SAME-ORIGIN `/xblock/…` under the default (empty) configuration.
 *
 * That prefix is currently claimed by the CMS in the dev proxy, because Studio's authoring
 * editor is served from the identically-named `/xblock/…` route and got there first. Both
 * services answer that path and they answer it differently: Studio's renders the authoring
 * editor behind a CMS session, the LMS's renders the block for a learner. Pointed at the
 * CMS, this URL is a 302 to Studio's login page inside the frame.
 *
 * The learner render therefore needs its own route to the LMS — either a distinct proxy
 * prefix or an absolute LMS origin in `VITE_LMS_ORIGIN`. Preserving the tenant's Host is
 * worth more than it looks: the LMS resolves the Site from it, and the resize protocol
 * below is switched on by a per-Site setting.
 * ---------------------------------------------------------------------------------
 */
export function unitFrameUrl(unitId: string, format?: string | null): string {
  const params = new URLSearchParams({
    view: 'student_view',
    show_title: '0',
    show_bookmark: '0',
    recheck_access: '1',
    preview: '0',
  });
  if (format) params.set('format', format);

  // '/learn-xblock' rather than '/xblock': the latter is proxied to Studio for authoring,
  // and the learner needs the LMS's render. The dev proxy rewrites this back to '/xblock'
  // on the way out, so the platform never sees our prefix.
  return `/learn-xblock/${encodeURIComponent(unitId)}?${params}`;
}

/**
 * WHAT THE PLATFORM'S FRAME IS ALLOWED TO DO — and the one thing it is not.
 *
 * A framed Open edX page can replace THIS page. That is not a theory; it is a script that
 * upstream puts in the `<head>` of nearly every LMS page, `lms/templates/main.html:50`:
 *
 *     % if not allow_iframing:
 *         if (window.location !== window.top.location) { window.top.location = window.location; }
 *
 * `allow_iframing` is set on exactly six views — the chromeless courseware render, the login
 * form, the LTI provider, the plugin API and the honor-code dialog. It is NOT set on the
 * error pages. `static_template_view.views.render` turns it on for `honor.html` and for
 * nothing else, so `server-error.html` — the "There has been a 500 error" page — inherits the
 * frame-buster and takes the top window with it.
 *
 * MEASURED on k8s dev, 2026-08-29, with a configured LTI Consumer in a unit that also held a
 * PDF, a poll, a survey and a word cloud. The LTI block's own launch frame requested
 * `…/handler/lti_launch_handler`, that handler answered 500, and the error page it returned
 * did this:
 *
 *     NAV sub   …/handler/lti_launch_handler     ← the block's launch frame. Expected.
 *     NAV MAIN  …/handler/lti_launch_handler     ← the learner's whole page. Not expected.
 *
 * The learner lost the outline, the progress bar, the Previous/Next controls and all FOUR
 * working components, because one component's third-party tool was misconfigured. Removing
 * the LTI block restored the unit.
 *
 * THE FIX IS NOT LTI-SPECIFIC AND MUST NOT BE. Nothing about that mechanism involves LTI: any
 * block whose handler errors, any 404, any redirect to a non-chromeless LMS page inside this
 * frame does the same thing. So the containment belongs on the frame, once, where it covers
 * every block type the platform has ever shipped and every one it gains later.
 *
 * `sandbox` is that containment. Omitting `allow-top-navigation` (and its by-user-activation
 * variant) makes `window.top.location = …` a no-op the browser refuses; the frame keeps its
 * own error, and the page around it survives. Everything else the platform legitimately does
 * is granted explicitly:
 *
 *   allow-same-origin              cookies — without it the frame is an opaque origin, the
 *                                  session does not travel and every unit is a login page.
 *                                  Also what lets us measure the frame's height and read
 *                                  which blocks it rendered.
 *   allow-scripts                  every XBlock ships its own JavaScript.
 *   allow-forms                    LTI 1.1 launches by POSTing a form; ORA, survey and poll
 *                                  submit through forms.
 *   allow-popups                   `launch_target: new_window` calls `window.open`, and the
 *                                  PDF block's download link opens in a new tab.
 *   allow-popups-to-escape-sandbox so a tool opened in a new window is a normal window and
 *                                  not a second sandbox — otherwise "open in a new window"
 *                                  would produce a tool that cannot keep its own session.
 *   allow-downloads                the PDF block's download link, and any block that hands
 *                                  the learner a file.
 *   allow-modals                   `window.confirm` is used by upstream's own block JS.
 *
 * KNOWN LIMIT, stated rather than glossed: `allow-same-origin` plus `allow-scripts` on
 * same-origin content is escapable in principle — the framed document could reach
 * `window.parent.document`, delete this attribute and reload itself. That is a deliberate act,
 * not something an error page does, and the alternative (dropping `allow-same-origin`) breaks
 * the session and the height measurement. The containment holds against the failure this
 * exists for; it is not a security boundary against hostile content we chose to host.
 */
export const PLATFORM_FRAME_SANDBOX = [
  'allow-same-origin',
  'allow-scripts',
  'allow-forms',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
  'allow-downloads',
  'allow-modals',
].join(' ');

/**
 * Whether the frame is allowed to talk to us at all.
 *
 * The render can be served from our own origin (through a proxy) or from the LMS's, so the
 * check is "is this the origin we asked for", derived from the URL we built rather than
 * assumed. Without it any framed page on the page could post a lookalike resize message
 * and drive the layout.
 */
export function frameOrigin(frameUrl: string): string {
  try {
    return new URL(frameUrl, window.location.href).origin;
  } catch {
    return window.location.origin;
  }
}

/**
 * The height the frame says it wants, or null for "not a message for us".
 *
 * `plugin.resize` is posted by the platform's own `courseware-chromeless.html`. It is
 * conditional, and the condition is worth writing down because a silent absence looks like
 * a bug in this file: the template only installs the reporter when the request's `Referer`
 * matches the Site's `LEARNING_MICROFRONTEND_URL`. An install that has not pointed that
 * setting at this app renders the unit perfectly and never says how tall it is — which is
 * why the caller also measures the document directly when it can.
 */
export function resizeHeight(data: unknown): number | null {
  if (!isResizeMessage(data)) return null;
  const height = data.payload?.height;
  return typeof height === 'number' && height > 0 ? height : null;
}

/* ------------------------------------------------------------------ display ----- */

/**
 * An icon and a colour class for a unit, from the platform's own `icon` value.
 *
 * The palette classes are the prototype's (`t-video`, `t-quiz`, …). Unknown values fall
 * back to the document treatment rather than guessing — new block types arrive with new
 * icon names, and an unrecognised one is still a unit worth listing.
 */
export function unitIcon(icon: string | null): { name: string; tone: string } {
  switch (icon) {
    case 'video':
      return { name: 'play_circle', tone: 't-video' };
    case 'problem':
      return { name: 'quiz', tone: 't-quiz' };
    case 'discussion':
      return { name: 'forum', tone: 't-discuss' };
    case 'openassessment':
      return { name: 'assignment', tone: 't-assign' };
    default:
      return { name: 'description', tone: 't-doc' };
  }
}

/** Percentage complete, rounded, safe on an empty course. */
export function progressPercent(done: number, total: number): number {
  return total === 0 ? 0 : Math.round((done / total) * 100);
}

/* --------------------------------------------------- reading counts as completing ---- */

/**
 * Blocks in a rendered unit that the PLATFORM says complete on being viewed.
 *
 * Read from the frame, never guessed. `xmodule/vertical_block.py` stamps
 * `data-mark-completed-on-view-after-delay` onto exactly those children whose completion mode
 * is completable, unscored and not self-managed — so a problem, a video and an open response
 * are all absent from this list by the platform's own reckoning, and none of them can be
 * completed by this route. That guarantee is the reason to read the attribute rather than
 * decide for ourselves what a "reading" block is.
 *
 * Returns [] across origins, where the document cannot be read at all.
 */
export function viewCompletableBlocks(frame: HTMLIFrameElement | null): string[] {
  const doc = frame?.contentDocument;
  if (!doc) return [];
  return [...doc.querySelectorAll<HTMLElement>('[data-mark-completed-on-view-after-delay]')]
    .map((element) => element.dataset['usageId'] ?? '')
    .filter((usageId) => usageId.startsWith('block-v1:'))
    // The unit's OWN wrapper carries the attribute too, and a vertical is an aggregator:
    // `submit_completion` refuses one with a 400. Measured — the request was harmless and
    // swallowed, but sending a call we know will be rejected is not something to leave in.
    .filter((usageId) => !usageId.includes('type@vertical'));
}

/**
 * Record these blocks as read.
 *
 * WHY THIS EXISTS, and why it is a deliberate difference from stock Open edX.
 *
 * The platform completes a text block only once its TOP AND BOTTOM have both been on screen
 * for a delay — `hasBeenViewed = topSeen && bottomSeen && seenFor >= viewedAfterMs` in
 * `ViewedEvent.js`. For a unit taller than the window that means scrolling to the very end.
 * Measured on a 2403px unit in a 1000px window: no completion at all after ten seconds of
 * reading, then complete within seconds of scrolling to the bottom.
 *
 * So a learner who reads what they need and presses Next leaves the unit unticked, and the
 * course sits at 0% however much of it they have been through. Pressing on to the next unit is
 * a deliberate act, and treating it as "I have read this" is the behaviour the academy asked
 * for.
 *
 * The write is the platform's own: `publish_completion` on the block's handler, the same
 * endpoint and the same `{completion: 1.0}` body that `CompletionOnViewService.js` posts. So
 * the record is indistinguishable from one earned by scrolling, and everything downstream —
 * the outline, the progress percentage, course completion, the certificate — sees it the same
 * way.
 *
 * Failures are swallowed on purpose: this fires as the learner navigates away, nothing on the
 * next screen depends on it, and a blocked or refused write must not interrupt them. A block
 * that was already complete is simply written again with the same value.
 */
export async function markBlocksRead(courseId: string, blockIds: string[]): Promise<void> {
  if (!courseId || blockIds.length === 0) return;

  await Promise.all(
    blockIds.map((blockId) =>
      openedx
        .post(
          `/courses/${encodeURIComponent(courseId)}/xblock/${encodeURIComponent(blockId)}/handler/publish_completion`,
          { completion: 1.0 },
        )
        .catch(() => undefined),
    ),
  );
}
